/**
 * CitationInline — Superscript citation badges with hover tooltips
 *
 * Renders [1], [2], [3] as small cyan badges within text.
 * Hover: tooltip with source title, domain, snippet.
 * Click: opens source URL in new tab.
 *
 * Also exports `renderTextWithCitations` helper that parses text
 * for citation patterns and replaces them with CitationInline components.
 */
import React, { useState, useRef, useEffect } from 'react';
import type { CitationInlineData, SourceRef } from '../types';

// =============================================================================
// FAVICON HELPER
// =============================================================================

function faviconUrl(domain: string): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=32`;
}

// =============================================================================
// SINGLE CITATION BADGE
// =============================================================================

interface CitationBadgeProps {
  num: number;
  source?: SourceRef;
}

const CitationBadge: React.FC<CitationBadgeProps> = ({ num, source }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const badgeRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Position tooltip safely
  const [tooltipSide, setTooltipSide] = useState<'above' | 'below'>('above');

  useEffect(() => {
    if (showTooltip && badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      // If badge is near top of viewport, show tooltip below
      setTooltipSide(rect.top < 160 ? 'below' : 'above');
    }
  }, [showTooltip]);

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setShowTooltip(false), 150);
  };

  const handleClick = () => {
    if (source?.url) {
      window.open(source.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <span className="relative inline-block align-super mx-0.5">
      <span
        ref={badgeRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleClick()}
        className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-md bg-cyan-400/15 text-cyan-400 border border-cyan-400/30 cursor-pointer hover:bg-cyan-400/25 hover:text-cyan-300 hover:border-cyan-400/50 transition-all duration-150 select-none leading-none"
        title={source ? source.title : `Source ${num}`}
      >
        {num}
      </span>

      {/* Tooltip */}
      {showTooltip && source && (
        <div
          ref={tooltipRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={`absolute z-50 w-64 p-3 rounded-xl bg-black/95 backdrop-blur-xl border border-cyan-400/25 shadow-[0_0_30px_rgba(34,211,238,0.15)] animate-in fade-in duration-150 ${
            tooltipSide === 'above'
              ? 'bottom-full mb-2 left-1/2 -translate-x-1/2'
              : 'top-full mt-2 left-1/2 -translate-x-1/2'
          }`}
        >
          {/* Domain row */}
          <div className="flex items-center gap-1.5 mb-1.5">
            <img
              src={source.favicon || faviconUrl(source.domain)}
              alt={source.domain}
              className="w-3.5 h-3.5 rounded-sm"
              onError={(e) => {
                (e.target as HTMLImageElement).src = faviconUrl(source.domain);
              }}
            />
            <span className="text-[10px] text-cyan-400/50 font-medium truncate">
              {source.domain}
            </span>
          </div>

          {/* Title */}
          <p className="text-xs font-semibold text-cyan-200 leading-snug line-clamp-2 mb-1">
            {source.title}
          </p>

          {/* Snippet */}
          {source.snippet && (
            <p className="text-[10px] text-cyan-400/40 leading-relaxed line-clamp-3">
              {source.snippet}
            </p>
          )}

          {/* Click hint */}
          <p className="text-[9px] text-cyan-400/25 mt-2 font-medium">
            Click to open source →
          </p>
        </div>
      )}
    </span>
  );
};

// =============================================================================
// CITATION INLINE — BLOCK COMPONENT
// =============================================================================

interface CitationInlineProps {
  data: CitationInlineData;
  sources: SourceRef[];
}

export const CitationInline: React.FC<CitationInlineProps> = ({ data, sources }) => {
  const { citations } = data;

  if (!citations || citations.length === 0) return null;

  return (
    <span className="inline">
      {citations.map((num) => {
        const source = sources.find((s) => s.id === num);
        return <CitationBadge key={num} num={num} source={source} />;
      })}
    </span>
  );
};

// =============================================================================
// HELPER: renderTextWithCitations
// =============================================================================

/**
 * Parses text for [1], [2] citation patterns and replaces them
 * with CitationBadge components. Returns an array of React nodes.
 *
 * Usage:
 *   const nodes = renderTextWithCitations("Paris is great [1] and big [2].", sources);
 *   return <p>{nodes}</p>;
 */
export function renderTextWithCitations(
  text: string,
  sources: SourceRef[]
): React.ReactNode[] {
  const pattern = /\[(\d+)\]/g;
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    // Text before citation
    if (match.index > lastIndex) {
      nodes.push(text.substring(lastIndex, match.index));
    }

    const num = parseInt(match[1], 10);
    const source = sources.find((s) => s.id === num);
    nodes.push(<CitationBadge key={`cite-${match.index}-${num}`} num={num} source={source} />);

    lastIndex = match.index + match[0].length;
  }

  // Remaining text
  if (lastIndex < text.length) {
    nodes.push(text.substring(lastIndex));
  }

  return nodes;
}

export default CitationInline;
